/**
 * A response, read as a flat list of field paths and values.
 *
 * A paired operation gets this from compare.js: the field summary and per-item tabs are field/value
 * tables. A single-sided one has nothing to pair with, so it has no comparison at all - which left
 * /me and /startup with the pretty-printed body as the only way to read them. Nesting is flattened
 * to a dotted path because both of those bodies nest: /startup carries the whole session under
 * `loggedInUser` and `userConfiguration`.
 */

/** Walked into. An empty object or array has nothing to walk, so it stays a value of its own. */
function isBranch(value) {
    return value != null && typeof value === "object" && Object.keys(value).length > 0;
}

/** Array members are indexed, object fields dotted - and the first field carries no dot. */
function childPath(parent, prefix, key) {
    if (Array.isArray(parent)) {
        return `${prefix}[${key}]`;
    }
    return prefix === "" ? key : `${prefix}.${key}`;
}

/**
 * @param {any} value the response item to flatten
 * @param {string} prefix the path `value` sits at - the recursion's own, and how a caller indexes
 *   one item of several ("[1]")
 * @returns {{path: string, value: any}[]} leaves in the order the API returned them, so the table
 *   reads the same way as the body printed next to it
 */
export function flattenProperties(value, prefix = "") {
    if (!isBranch(value)) {
        // an item that is a bare scalar has no field name to show; name it after the body itself
        return [{path: prefix === "" ? "(body)" : prefix, value}];
    }
    return Object.entries(value)
        .flatMap(([key, child]) => flattenProperties(child, childPath(value, prefix, key)));
}
